import { ChatStore } from 'src/app/chat/chat-store.service';
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, inject, HostListener, ViewChildren, QueryList } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { CommonModule, NgClass, NgTemplateOutlet } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { localStorageKeys, userRoleNameEnum } from '../../const/enum';
import { TAB_CONFIG, TAB_ORDER } from '../../const/tab-config';
import { hasConsoleLogRole, isConsoleLogAllowedEmail } from '../../utils/console-log-access';
import { AuthService } from 'src/app/services/auth.service';
import { UtilService } from 'src/app/services/util.service';
import { GroupService } from 'src/app/services/group.service';
import { RolesService } from 'src/app/services/roles.service';
import { SiteService } from 'src/app/services/site.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  imports: [RouterLink, CommonModule, NgTemplateOutlet, NgbTooltip]
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() mobileOpen = false;
  @Output() collapsedEvent = new EventEmitter<boolean>();

  @ViewChildren(NgbTooltip) private tooltips!: QueryList<NgbTooltip>;

  private router = inject(Router);
  /** How many chats have unread messages, shown on the Chat menu item. The header starts the store. */
  chatStore = inject(ChatStore);
  private authService = inject(AuthService);
  private utilService = inject(UtilService);
  private groupService = inject(GroupService);
  private rolesService = inject(RolesService);
  private siteService = inject(SiteService);

  /**
   * The two incident tabs share one sidebar row.
   *
   * They remain two permissions over two pages — Incident History is the site-grouped view
   * of every message record, Incident Report the flat, date-filtered list of the
   * incident-report ones — so which page the row opens is still decided per role: a role
   * carrying only the report permission lands on the report page exactly as before. What
   * goes away is the pair of rows. Where a role has both, History stands for both: its page
   * already lists report records, and its `message_unread` badge already counts them, so a
   * second row was a second door onto a subset of the same thing.
   */
  private static readonly INCIDENT_TAB_KEYS = ['incident-message', 'incident-report'];
  private static readonly INCIDENT_TAB_LABEL = 'Incident History';

  // Tab permissions for the logged-in user's role (null until loaded).
  private tabPermissions: { [key: string]: boolean } | null = null;
  // Dynamic tab catalog fetched from the DB (list of { key, label }).
  private catalog: any[] = [];

  collapsed = true;
  isToggleLocked = false;
  private readonly toggleLockMs = 320;
  currentPath = '';
  private routerSub?: Subscription;
  private roleListSub?: Subscription;
  private catalogChangedSub?: Subscription;
  token: string;
  roleType: string = '';
  // Fallback master list shown only until the DB catalog loads. Once the
  // catalog arrives, buildMenu() replaces this with the dynamic tab list.
  sideBarItem: any[] = [
    { name: 'Dashboard', routerLink: '/new-dashboard', icon: 'fa fa-home' },
    { name: 'Users', routerLink: '/site-admins', icon: 'fa fa-user' },
    { name: 'Sites', routerLink: '/sites', icon: 'fa fa-building-o' },
    { name: 'Hierarchy', routerLink: '/role', icon: 'fa fa-user' },
    // SCHEDULE TEMPLATES DISABLED
    // { name: 'Templates', routerLink: '/templates', icon: 'fa fa-file' },
    { name: 'Dynamic Form', routerLink: '/Dynamic-Form', icon: 'fa fa-file-text' },
    // One incident row, as buildMenu() renders it once the catalog is in.
    { name: 'Incident History', routerLink: '/alert-message-info', icon: 'fa fa-bell', count: 0 },
    { name: 'User Logs', routerLink: '/user-logs', icon: 'fa fa-list-alt' },
    { name: 'Console Log', routerLink: '/console-log', icon: 'fa fa-terminal' },
    { name: 'Unknown Site', routerLink: '/unknown-site', icon: 'fa fa-map-marker' },
    { name: 'Reports', routerLink: '/reports', icon: 'fa fa-bar-chart' },
    { name: 'Memory Status', routerLink: '/memory-status', icon: 'fa fa-microchip' },
    { name: 'Settings', routerLink: '/settings', icon: 'fa fa-cog' }
  ];
  private defaultUnreadCount = 0;
  private incidentReportUnreadCount = 0;
  // Badge on the Inactive Sites tab: how many sites have had no alert in the last 24h.
  // The same /sites/list call the page makes returns it, so this is one extra request
  // per session rather than a dedicated endpoint.
  private inactiveSiteCount = 0;
  private inactiveSiteCountSub?: Subscription;
  // buildMenu() runs on every catalog / permission / role signal, so without this the
  // badge would refetch several times per page load.
  private inactiveSiteCountFetched = false;

  constructor() { }

  ngOnInit(): void {
    this.currentPath = this.router.url.split('?')[0];
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentPath = event.urlAfterRedirects.split('?')[0];
      });

    this.token = this.utilService.getLocalStorage(localStorageKeys.accessToken);

    // Load the DB tab catalog + the role's tab permissions, then build the menu.
    this.loadCatalog();
    this.loadTabPermissions();

    // A role add/update can change which tabs this user may see, so re-read the
    // permissions (not the catalog — the tab list itself did not change).
    this.roleListSub = this.rolesService.roleList.subscribe((res: any) => {
      if (res) {
        this.loadTabPermissions();
      }
    });

    // The catalog only changes when a tab is added / renamed / removed, which is
    // exactly what this signal reports.
    this.catalogChangedSub = this.rolesService.permissionCatalogChanged.subscribe(() => {
      this.loadCatalog();
    });

    this.authService.userRole.subscribe((res: any) => {
      if (res) {
        this.setMenu(res);
      }
    });
    if (this.utilService.loginUserType) {
      this.setMenu(this.utilService.loginUserType);
    } else {
      this.setMenu(userRoleNameEnum.superAdmin);
    }

    this.groupService.unreadCount$.subscribe((count) => {
      this.defaultUnreadCount = count;
      this.updateMessageLabel();
    });

    this.groupService.incidentReportUnreadCount$.subscribe((count) => {
      this.incidentReportUnreadCount = count;
      this.updateMessageLabel();
    });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.roleListSub?.unsubscribe();
    this.catalogChangedSub?.unsubscribe();
    this.inactiveSiteCountSub?.unsubscribe();
  }

  @HostListener('document:visibilitychange')
  onDocumentVisibilityChange(): void {
    if (document.visibilityState !== 'visible') {
      return;
    }
    // The message/alert counters are not refreshed here any more — GroupService polls
    // them on a fixed 30s timer, so a refocus would only add a duplicate request.
    this.loadInactiveSiteCount(true);
  }

  setMenu(type) {
    this.roleType = type;

    /*
     * Role-wise static menus are commented out on purpose. The sidebar is now
     * built dynamically from the DB tab catalog + the role's saved permissions
     * (see buildMenu()), so these hardcoded per-role lists no longer take
     * precedence over the dynamic permissions.
     *
    if (type === userRoleNameEnum.operator || type === userRoleNameEnum.support) {
      this.sideBarItem = [
        { name: 'Dashboard', routerLink: '/new-dashboard', icon: 'fa fa-home' },
        { name: 'Sites', routerLink: '/sites', icon: 'fa fa-building-o' },
        { name: 'Hierarchy', routerLink: '/role', icon: 'fa fa-user' },
        // { name: 'Group', routerLink: '/group', icon: 'fa fa-users' },
        { name: 'User Logs', routerLink: '/user-logs', icon: 'fa fa-list-alt' },
        { name: 'Reports', routerLink: '/reports', icon: 'fa fa-bar-chart' },
        { name: 'Memory Status', routerLink: '/memory-status', icon: 'fa fa-microchip' },
        { name: 'Settings', routerLink: '/settings', icon: 'fa fa-cog' }
      ];
    } else if (type === userRoleNameEnum.superAdmin || type === userRoleNameEnum.manager) {
      this.sideBarItem = [
        { name: 'Dashboard', routerLink: '/new-dashboard', icon: 'fa fa-home' },
        { name: 'Users', routerLink: '/site-admins', icon: 'fa fa-user' },
        { name: 'Sites', routerLink: '/sites', icon: 'fa fa-building-o' },
        { name: 'Hierarchy', routerLink: '/role', icon: 'fa fa-user' },
        // SCHEDULE TEMPLATES DISABLED
        // { name: 'Templates', routerLink: '/templates', icon: 'fa fa-file' },
        { name: 'Dynamic Form', routerLink: '/Dynamic-Form', icon: 'fa fa-file-text' },
        { name: 'Incident History', routerLink: '/alert-message-info', icon: 'fa fa-bell', count: 0 },
        { name: 'User Logs', routerLink: '/user-logs', icon: 'fa fa-list-alt' },
        ...(type === userRoleNameEnum.superAdmin
          ? [
            { name: 'Console Log', routerLink: '/console-log', icon: 'fa fa-terminal' },
            { name: 'Unknown Site', routerLink: '/unknown-site', icon: 'fa fa-map-marker' }
          ]
          : []),
        { name: 'Reports', routerLink: '/reports', icon: 'fa fa-bar-chart' },
        { name: 'Memory Status', routerLink: '/memory-status', icon: 'fa fa-microchip' },
        { name: 'Settings', routerLink: '/settings', icon: 'fa fa-cog' }
      ];
    } else if (type === userRoleNameEnum.supervisor) {
      this.sideBarItem = [
        { name: 'Dashboard', routerLink: '/new-dashboard', icon: 'fa fa-home' },
        { name: 'Settings', routerLink: '/settings', icon: 'fa fa-cog' }
      ];
    } else if (type === userRoleNameEnum.clientSupport) {
      this.sideBarItem = [
        { name: 'Dashboard', routerLink: '/client-support/dashboard', icon: 'fa fa-home' },
        { name: 'Settings', routerLink: '/client-support/settings', icon: 'fa fa-cog' },
      ];
    } else if (type === userRoleNameEnum.IT) {
      this.sideBarItem = [
        { name: 'Dashboard', routerLink: '/new-dashboard', icon: 'fa fa-home' },
        { name: 'Users', routerLink: '/site-admins', icon: 'fa fa-user' },
        { name: 'Sites', routerLink: '/sites', icon: 'fa fa-building-o' },
        { name: 'Hierarchy', routerLink: '/role', icon: 'fa fa-user' },
        // SCHEDULE TEMPLATES DISABLED
        // { name: 'Templates', routerLink: '/templates', icon: 'fa fa-file' },
        { name: 'Dynamic Form', routerLink: '/Dynamic-Form', icon: 'fa fa-file-text' },
        { name: 'Incident Report', routerLink: '/incident-reports', icon: 'fa fa-file-text-o', count: 0 },
        { name: 'Incident Message', routerLink: '/alert-message-info', icon: 'fa fa-bell', count: 0 },
        { name: 'User Logs', routerLink: '/user-logs', icon: 'fa fa-list-alt' },
        { name: 'Reports', routerLink: '/reports', icon: 'fa fa-bar-chart' },
        { name: 'Memory Status', routerLink: '/memory-status', icon: 'fa fa-microchip' },
        { name: 'Settings', routerLink: '/settings', icon: 'fa fa-cog' },
      ];
    } else if (type === userRoleNameEnum.Client) {
      this.sideBarItem = [
        { name: 'Dashboard', routerLink: '/new-dashboard', icon: 'fa fa-home' },
        { name: 'Settings', routerLink: '/settings', icon: 'fa fa-cog' },
      ];
    }
    */

    // Build the menu dynamically from the DB catalog + permissions.
    this.buildMenu();

    this.defaultUnreadCount = this.groupService.unreadCount$.value;
    this.incidentReportUnreadCount = this.groupService.incidentReportUnreadCount$.value;
    this.updateMessageLabel();
  }

  // Fetch the DB tab catalog, then rebuild the menu.
  private loadCatalog(): void {
    this.rolesService.getPermissionsCatalog().subscribe({
      next: (res: any) => {
        this.catalog = Array.isArray(res) ? res.filter((t: any) => !t.deleted) : [];
        this.buildMenu();
      },
      error: (e) => console.log(e),
    });
  }

  // Fetch the logged-in user's tab permissions, then rebuild the menu.
  private loadTabPermissions(): void {
    this.rolesService.getTabPermissions().subscribe({
      next: (res: any) => {
        this.tabPermissions = res && res.permissions ? res.permissions : null;
        this.buildMenu();
      },
      error: (e) => console.log(e),
    });
  }

  private buildMenu(): void {
    if (!this.catalog || !this.catalog.length) {
      return;
    }
    const visible = this.catalog.filter((tab: any) => this.canSeeTab(tab?.key));
    // Incident History and Incident Report are one row now — see INCIDENT_TAB_LABEL.
    const hasIncidentHistory = visible.some((tab: any) => tab?.key === 'incident-message');
    this.sideBarItem = visible
      .filter((tab: any) => !(tab?.key === 'incident-report' && hasIncidentHistory))
      .slice()
      .sort((a: any, b: any) => this.tabOrder(a?.key) - this.tabOrder(b?.key))
      .map((tab: any) => {
        const cfg = TAB_CONFIG[tab.key];
        if (!cfg) {
          return null;
        }
        const name = SidebarComponent.INCIDENT_TAB_KEYS.includes(tab.key)
          ? SidebarComponent.INCIDENT_TAB_LABEL
          : tab.label;
        // Inactive Sites carries a live indicator (pinned to the icon, pulsing) rather
        // than the plain end-of-row count the message tabs use.
        return {
          name,
          routerLink: cfg.routerLink,
          iconKey: cfg.icon,
          count: 0,
          liveBadge: tab.key === 'inactive-site',
        };
      })
      .filter((item: any) => !!item);
    this.updateMessageLabel();
    this.loadInactiveSiteCount();
  }

  private canSeeTab(key: string): boolean {
    if (key === 'console-log' || key === 'unknown-site') {
      return this.canSeeConsoleLog(key);
    }
    return this.isTabPermitted(key);
  }

  private isTabPermitted(key: string): boolean {
    return !this.tabPermissions || this.tabPermissions[key] !== false;
  }

  private canSeeConsoleLog(key: string): boolean {
    const user = this.utilService.getLocalStorage(localStorageKeys.userDetails);
    if (isConsoleLogAllowedEmail(user)) {
      return true;
    }

    const roleType = this.utilService.loginUserType
      || this.utilService.getLocalStorage(localStorageKeys.roleType);

    return this.isTabPermitted(key) && hasConsoleLogRole(user, roleType);
  }

  // Position in TAB_ORDER; anything not listed there sorts after, keeping the
  // catalog's own order among themselves.
  private tabOrder(key: string): number {
    const index = TAB_ORDER.indexOf(key);
    return index === -1 ? TAB_ORDER.length : index;
  }

  /**
   * Badge count for the Inactive Sites tab.
   *
   * Only the count is wanted, so ask for the smallest possible page: `counts` covers
   * the whole set regardless of pagination. Fetched once per session unless `force`
   * (the tab regaining focus), and never before login — the sidebar is constructed
   * while the login screen is up, where an unauthenticated /sites/list just 401s.
   */
  private loadInactiveSiteCount(force = false): void {
    if (!this.sideBarItem.some((item) => item.routerLink === '/inactive-sites')) {
      return;
    }
    if (!this.utilService.getLocalStorage(localStorageKeys.accessToken)) {
      return;
    }
    if (this.inactiveSiteCountFetched && !force) {
      return;
    }
    this.inactiveSiteCountFetched = true;
    this.inactiveSiteCountSub?.unsubscribe();
    this.inactiveSiteCountSub = this.siteService.getSitesList({
      pagination: { page: 1, perPage: 1 },
      sort: { created_at: -1 },
      search: null,
      query: {},
      statusFilter: 'Inactive',
    }).subscribe({
      next: (res: any) => {
        this.inactiveSiteCount = Number(res?.counts?.inactive) || 0;
        this.updateMessageLabel();
      },
      error: (e) => {
        // Let a later trigger (tab focus, menu rebuild) try again.
        this.inactiveSiteCountFetched = false;
        console.log(e);
      },
    });
  }

  private updateMessageLabel() {
    // Keyed by route, so the single incident row carries the count of whichever page it
    // opens: the default unread count on /alert-message-info — which already includes the
    // incident-report records that page lists — and the incident-report count on
    // /incident-reports, for a role that only has that permission.
    const defaultCount = Number(this.defaultUnreadCount) || 0;
    const incidentReportCount = Number(this.incidentReportUnreadCount) || 0;
    this.sideBarItem = this.sideBarItem.map(menu => {
      if (menu.routerLink === '/alert-message-info') {
        return { ...menu, count: defaultCount };
      }
      if (menu.routerLink === '/incident-reports') {
        return { ...menu, count: incidentReportCount };
      }
      if (menu.routerLink === '/inactive-sites') {
        return { ...menu, count: this.inactiveSiteCount };
      }
      return menu;
    });
  }

  toggleCollapsed(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isToggleLocked) {
      return;
    }

    this.closeAllTooltips();
    (document.activeElement as HTMLElement | null)?.blur();

    this.isToggleLocked = true;
    this.collapsed = !this.collapsed;
    this.collapsedEvent.emit(this.collapsed);

    window.setTimeout(() => {
      this.isToggleLocked = false;
    }, this.toggleLockMs);
  }

  closeAllTooltips(): void {
    this.tooltips?.forEach((tooltip) => tooltip.close());
  }

  isSettingsRoute(routerLink: string): boolean {
    return routerLink === '/settings' || routerLink === '/client-support/settings';
  }

  isExactRoute(routerLink: string): boolean {
    return routerLink === '/new-dashboard'
      || routerLink === '/dashboard'
      || routerLink === '/client-support/dashboard';
  }

  isRouteActive(routerLink: string): boolean {
    if (!this.currentPath) {
      return false;
    }

    if (this.isExactRoute(routerLink)) {
      return this.currentPath === routerLink;
    }

    return this.currentPath === routerLink
      || this.currentPath.startsWith(`${routerLink}/`);
  }

  getMenuIconKey(routerLink: string): string {
    const iconMap: Record<string, string> = {
      '/dashboard': 'dashboard',
      '/new-dashboard': 'dashboard',
      '/client-support/dashboard': 'dashboard',
      '/users': 'users',
      '/site-admins': 'users',
      '/clients': 'users',
      '/sites': 'sites',
      '/inactive-sites': 'site-off',
      '/role': 'hierarchy',
      '/templates': 'templates',
      '/Dynamic-Form': 'form',
      '/alert-message-info': 'bell',
      '/incident-reports': 'clipboard',
      '/chat': 'chat',
      '/user-logs': 'logs',
      '/console-log': 'terminal',
      '/unknown-site': 'unknown-site',
      '/reports': 'reports',
      '/memory-status': 'memory',
      '/group': 'users',
      '/settings': 'settings',
      '/client-support/settings': 'settings',
    };
    return iconMap[routerLink] ?? 'settings';
  }
}
